import { FilesFiltersValues } from '@picsart/cloud-storage/core/enums/files';

import { APP_DRIVE_URL } from '@/constants';
import { Gender, HairColor } from '@/constants/enums';
import type { LibraryCharacter, LookVariant } from '@/types/library';

import { getContext, isContextAvailable } from '../miniappContext';

import { createLookSubfolder, deleteCharacterFolderFromDrive, getCrudService, getMyLooksFolderUid, saveImageToFolder, saveJsonToFolder } from './driveFolderService';

// ============================================================================
// LIBRARY JSON — characters are persisted as one hidden JSON per character
// inside their own subfolder of "My Looks". No global library file is written.
// ============================================================================

/**
 * Eagerly persist a character to Drive: creates a subfolder, saves the
 * character image, and writes the hidden JSON snapshot. Returns the subfolder
 * uid + name so the caller can persist them on the LibraryCharacter; later
 * generations reuse the same folder instead of creating a duplicate.
 */
export async function saveCharacterToDrive(character: LibraryCharacter): Promise<{ uid: string; name: string } | null> {
  if (!isContextAvailable()) return null;
  let uid: string | null = null;
  try {
    const name = `Look-${character.id.slice(0, 8)}-${Date.now()}`;
    uid = await createLookSubfolder(name);
    if (!uid) return null;

    const jsonId = await saveJsonToFolder(JSON.stringify(character), 'ai-wardrobe-character.json', uid);
    if (!jsonId) {
      void deleteCharacterFolderFromDrive(uid);
      return null;
    }
    if (character.characterImageUrl) {
      await saveImageToFolder(character.characterImageUrl, uid);
    }
    return { uid, name };
  } catch (err) {
    console.error('[DriveLibraryService] saveCharacterToDrive error:', err);
    if (uid) void deleteCharacterFolderFromDrive(uid);
    return null;
  }
}

/**
 * Load all characters by walking each subfolder inside "My Looks" and reading
 * its hidden `ai-wardrobe-character.json`. Aggregates the results into
 * LibraryCharacter[] (returns null when nothing is available).
 */
export const loadLibraryFromDrive = async (): Promise<LibraryCharacter[] | null> => {
  if (!isContextAvailable()) {
    return null;
  }
  try {
    const myLooksUid = await getMyLooksFolderUid();
    if (!myLooksUid) return null;

    const crud = await getCrudService();

    // Page through subfolders until a short page returns. Backend caps limit
    // at 128; we use 100 to stay safely under it. Hard cap at 50 pages
    // (5000 characters) to bound the loop on a degenerate response.
    const PAGE_SIZE = 100;
    const MAX_PAGES = 50;
    const subfolders: Array<{ uid: string; name: string }> = [];
    for (let page = 0; page < MAX_PAGES; page++) {
      const offset = page * PAGE_SIZE;
      const foldersResp = await crud.getFiles({
        params: { parentFolderUid: myLooksUid, limit: String(PAGE_SIZE), offset: String(offset) },
        filters: [FilesFiltersValues.Folder],
      });
      if (foldersResp.status !== 'success' || !Array.isArray(foldersResp.response)) {
        if (offset === 0) return null;
        break;
      }
      const batch = foldersResp.response as Array<{ uid: string; name: string }>;
      if (batch.length === 0) break;
      subfolders.push(...batch);
      if (batch.length < PAGE_SIZE) break;
    }
    if (subfolders.length === 0) return null;

    const ctx = getContext();

    const characters: Array<LibraryCharacter | null> = await Promise.all(
      subfolders.map(async folder => {
        const folderImages = await listImageFilesInFolder(folder.uid);
        const fileId = await findCharacterJsonInFolder(folder.uid);

        let parsed: LibraryCharacter | null = null;

        if (fileId) {
          try {
            // The /drive/v1/files/{id}/download endpoint produces application/octet-stream;
            // SDK's getTextResourceByFileId sets a non-matching Accept and gets rejected.
            // Use a raw fetch with Accept: */* via the miniapps SDK's authenticated fetch.
            const resp = await ctx.utils.api.fetch(`${APP_DRIVE_URL}/drive/v1/files/${fileId}/download`, {
              headers: { Accept: '*/*' },
            });
            if (resp.ok) {
              const text = await resp.text();
              if (text) {
                const candidate = JSON.parse(text) as LibraryCharacter | null;
                if (candidate && typeof candidate === 'object' && 'id' in candidate) {
                  parsed = candidate;
                }
              }
            }
          } catch (err) {
            console.error('[DriveLibraryService] loadLibraryFromDrive read/parse failed for', folder.name, err);
          }
        }

        // Fallback: no JSON yet (legacy folder, or save-after-generate hasn't
        // landed before reload). Synthesize a minimal LibraryCharacter from
        // the folder's visible files — character-reference becomes the
        // character image; everything else becomes orphan looks in the
        // reconcile pass below.
        if (!parsed) {
          const charRef = folderImages.find(f => f.name === 'character-reference');
          if (!charRef) return null;
          parsed = {
            id: folder.uid,
            characterImageUrl: charRef.sourceUrl,
            driveUidCharacter: charRef.uid,
            timestamp: charRef.createdOn ? new Date(charRef.createdOn).getTime() : Date.now(),
            gender: Gender.Female,
            looks: [],
          };
        }

        const presentUids = new Set(folderImages.map(f => f.uid));

        // Drop the character entirely if its reference file was deleted from Drive.
        // Only enforced when the JSON expected a uid; legacy entries without one stay.
        if (parsed.driveUidCharacter && !presentUids.has(parsed.driveUidCharacter)) {
          return null;
        }

        // Prune looks whose generated image was deleted from Drive.
        const existingLooks = Array.isArray(parsed.looks) ? parsed.looks : [];
        const survivingLooks = existingLooks.filter(l => !l.driveUidGenerated || presentUids.has(l.driveUidGenerated));
        const prunedCount = existingLooks.length - survivingLooks.length;

        // Reconcile orphaned look images: the workflow auto-saves generated
        // images via driveOptions.folderName, but the JSON only gets updated
        // when the client receives the result. If the user reloads mid-flight,
        // or if the JSON was just synthesized, looks[] is missing the images
        // already in Drive. Pick them up here so the user sees their work.
        const knownLookUids = new Set(survivingLooks.map(l => l.driveUidGenerated).filter((x): x is string => Boolean(x)));
        const orphanFiles = folderImages.filter(f => f.uid && f.name !== 'character-reference' && !knownLookUids.has(f.uid));
        let mergedLooks = survivingLooks;
        if (orphanFiles.length > 0) {
          const orphanLooks: LookVariant[] = orphanFiles.map(f => ({
            id: f.uid,
            generatedImageUrl: f.sourceUrl,
            driveUidGenerated: f.uid,
            timestamp: f.createdOn ? new Date(f.createdOn).getTime() : Date.now(),
            selectedItems: [],
            selectedStyle: null,
            selectedPose: null,
            selectedBackground: null,
            selectedHairstyle: null,
            selectedHairColor: HairColor.Natural,
          }));
          mergedLooks = [...orphanLooks, ...survivingLooks].sort((a, b) => b.timestamp - a.timestamp);
        }

        // Persist when anything changed so we don't re-reconcile every load.
        // For synthesized characters, this also creates the JSON for the first time.
        if (orphanFiles.length > 0 || prunedCount > 0) {
          void saveJsonToFolder(JSON.stringify({ ...parsed, looks: mergedLooks }), 'ai-wardrobe-character.json', folder.uid);
        }

        const stamped: LibraryCharacter = { ...parsed, looks: mergedLooks, driveSubfolderUid: folder.uid, driveSubfolderName: folder.name };
        return stamped;
      }),
    );

    const valid = characters.filter((c): c is LibraryCharacter => c !== null);
    return valid.length > 0 ? valid : null;
  } catch (err) {
    console.error('[DriveLibraryService] loadLibraryFromDrive outer error:', err);
    return null;
  }
};

/**
 * List non-hidden image files inside a Drive subfolder. Used by
 * loadLibraryFromDrive to detect look images that the workflow auto-saved
 * via driveOptions.folderName but that never made it into the per-character
 * JSON (e.g. user reloaded before the client could persist the look).
 */
interface DriveImageEntry {
  uid: string;
  name: string;
  sourceUrl: string;
  createdOn?: string;
}

async function listImageFilesInFolder(folderId: string): Promise<DriveImageEntry[]> {
  try {
    const crud = await getCrudService();
    const result = await crud.getFiles({
      params: { parentFolderUid: folderId, limit: '100', offset: '0' },
    });
    if (result.status !== 'success' || !Array.isArray(result.response)) return [];
    return (result.response as Array<Partial<DriveImageEntry> & { id?: string }>)
      .map(item => ({
        uid: item.uid ?? item.id ?? '',
        name: item.name ?? '',
        sourceUrl: item.sourceUrl ?? '',
        createdOn: item.createdOn,
      }))
      .filter(item => item.uid && item.sourceUrl);
  } catch (err) {
    console.error('[DriveLibraryService] listImageFilesInFolder error:', err);
    return [];
  }
}

async function findCharacterJsonInFolder(folderId: string): Promise<string | null> {
  try {
    const crud = await getCrudService();
    const result = await crud.getFiles({
      params: { parentFolderUid: folderId, limit: '50', offset: '0' },
      onlyHiddenFiles: true,
    });
    if (result.status !== 'success' || !Array.isArray(result.response)) return null;
    const match = result.response.find(item => item.name === 'ai-wardrobe-character.json');
    if (!match) {
      return null;
    }
    return (match as { uid?: string; id?: string }).uid ?? (match as { id?: string }).id ?? null;
  } catch (err) {
    console.error('[DriveLibraryService] findCharacterJsonInFolder error:', err);
    return null;
  }
}

// Disabled: the global library JSON at "My Looks/ai-wardrobe-library.json" is
// no longer written. Each character's data lives inside its own subfolder JSON
// (see saveCharacterToDrive + saveJsonToFolder). Kept as a no-op so the
// existing store-action callers (setGender, addLookToCharacter,
// deleteCharacter, …) don't need to change.
export const saveLibraryToDrive = (entries: LibraryCharacter[]): Promise<boolean> => {
  void entries;
  return Promise.resolve(true);
};
